import { uiSurface } from "shared/ui/uiSurface"
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import dayjs, { Dayjs } from "dayjs"
import { FC, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  ImmobilityReportItem,
  ImmobilityReportResponse,
  useFetchImmobilityReportMutation,
} from "../../apis/appApi"

const getSeverityColor = (hours: number | null): string => {
  if (hours == null) return "transparent"
  if (hours >= 12) return "rgba(211, 47, 47, 0.12)" // red
  if (hours >= 8) return "rgba(237, 108, 2, 0.12)" // orange
  if (hours >= 4) return "rgba(237, 201, 2, 0.12)" // yellow
  return "transparent"
}

const getSeverityLevel = (hours: number | null): "critical" | "high" | "medium" | "low" => {
  if (hours == null) return "low"
  if (hours >= 12) return "critical"
  if (hours >= 8) return "high"
  if (hours >= 4) return "medium"
  return "low"
}

export const ImmobilityTab: FC = () => {
  const { t } = useTranslation()

  // State
  const [deliveryDate, setDeliveryDate] = useState<Dayjs>(dayjs())
  const [thresholdHours, setThresholdHours] = useState<number>(4)
  const [report, setReport] = useState<ImmobilityReportResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  const [fetchImmobilityReport, { isLoading }] = useFetchImmobilityReportMutation()

  const handleFetch = useCallback(async () => {
    setError(null)
    try {
      const result = await fetchImmobilityReport({
        deliveryDate: deliveryDate.format("YYYY-MM-DD"),
        thresholdHours,
      }).unwrap()
      setReport(result)
    } catch (err: any) {
      setError(err?.data?.message || t("reports.immobility.errors.fetchFailed"))
    }
  }, [deliveryDate, thresholdHours, fetchImmobilityReport, t])

  // Summary counts by severity
  const summaryCounts = useMemo(() => {
    if (!report) return { critical: 0, high: 0, medium: 0, total: 0 }
    let critical = 0
    let high = 0
    let medium = 0
    for (const item of report.items) {
      const level = getSeverityLevel(item.hoursStuck)
      if (level === "critical") critical++
      else if (level === "high") high++
      else medium++
    }
    return { critical, high, medium, total: report.totalStuckOrders }
  }, [report])

  // State label map
  const stateLabel = useCallback(
    (state: string) => {
      const key = `reports.immobility.state.${state}`
      const val = t(key)
      return val === key ? state : val
    },
    [t],
  )

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return t("reports.immobility.unknown")
    try {
      return new Date(dateStr).toLocaleString([], {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    } catch {
      return dateStr
    }
  }

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "reference",
        headerName: t("reports.fields.reference"),
        flex: 1,
        minWidth: 120,
      },
      {
        field: "clientName",
        headerName: t("reports.fields.customer"),
        flex: 1.5,
        minWidth: 160,
      },
      {
        field: "state",
        headerName: t("reports.immobility.fields.status"),
        width: 200,
        renderCell: (params: any) => (
          <Chip
            label={stateLabel(params.value)}
            size="small"
            color={
              params.value === "READY_FOR_HAULAGE" ? "warning" : "info"
            }
            variant="outlined"
          />
        ),
      },
      {
        field: "stuckSince",
        headerName: t("reports.immobility.fields.stuckSince"),
        width: 180,
        valueGetter: (_value: any, row: any) => formatDateTime(row.stuckSince),
      },
      {
        field: "hoursStuck",
        headerName: t("reports.immobility.fields.hoursStuck"),
        width: 130,
        align: "right",
        headerAlign: "right",
        valueGetter: (_value: any, row: any) =>
          row.hoursStuck != null ? `${row.hoursStuck}h` : "--",
      },
      {
        field: "lastEvent",
        headerName: t("reports.immobility.fields.lastEvent"),
        width: 200,
        valueGetter: (_value: any, row: any) => row.lastEvent || "--",
      },
      {
        field: "haulerEmployeeName",
        headerName: t("reports.fields.courier"),
        width: 160,
        valueGetter: (_value: any, row: any) =>
          row.haulerEmployeeName || t("reports.immobility.unassigned"),
      },
    ],
    [t, stateLabel],
  )

  const gridRows = useMemo(() => {
    if (!report) return []
    return report.items.map((item: ImmobilityReportItem) => ({
      id: item.orderId,
      reference: item.reference,
      clientName: item.clientName,
      state: item.state,
      stuckSince: item.stuckSince,
      hoursStuck: item.hoursStuck,
      lastEvent: item.lastEvent,
      haulerEmployeeName: item.haulerEmployeeName,
    }))
  }, [report])

  return (
    <Box
      {...uiSurface("UI-DLV2-195")}
      data-testid="immobility-tab"
      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
    >
      {/* Controls */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", md: "center" }}
        flexWrap="wrap"
      >
        <Box data-testid="immobility-date-picker">
          <DatePicker
            label={t("reports.immobility.deliveryDate")}
            value={deliveryDate}
            onChange={(v) => v && setDeliveryDate(v)}
            slotProps={{
              textField: {
                size: "small",
                sx: { width: { xs: "100%", md: 180 } },
              },
            }}
          />
        </Box>
        <TextField
          data-testid="immobility-threshold-input"
          label={t("reports.immobility.thresholdHours")}
          type="number"
          size="small"
          value={thresholdHours}
          onChange={(e) => {
            const val = parseInt(e.target.value, 10)
            if (!isNaN(val) && val >= 1 && val <= 72) setThresholdHours(val)
          }}
          inputProps={{ min: 1, max: 72 }}
          sx={{ width: { xs: "100%", md: 140 } }}
        />
        <Button
          data-testid="immobility-fetch-btn"
          variant="contained"
          onClick={handleFetch}
          disabled={isLoading}
          sx={{ width: { xs: "100%", md: "auto" } }}
        >
          {isLoading ? t("loading") : t("reports.immobility.actions.fetch")}
        </Button>
      </Stack>

      {error && (
        <Alert
          data-testid="immobility-error-alert"
          severity="error"
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      {report && (
        <>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <Card
              data-testid="immobility-total-card"
              variant="outlined"
              sx={{ minWidth: 140 }}
            >
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {t("reports.immobility.summary.total")}
                </Typography>
                <Typography variant="h6">{summaryCounts.total}</Typography>
              </CardContent>
            </Card>
            <Card
              data-testid="immobility-critical-card"
              variant="outlined"
              sx={{
                minWidth: 140,
                borderColor: "error.main",
              }}
            >
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="caption" color="error.main">
                  {t("reports.immobility.summary.critical")}
                </Typography>
                <Typography variant="h6" color="error.main">
                  {summaryCounts.critical}
                </Typography>
              </CardContent>
            </Card>
            <Card
              data-testid="immobility-high-card"
              variant="outlined"
              sx={{
                minWidth: 140,
                borderColor: "warning.main",
              }}
            >
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="caption" color="warning.main">
                  {t("reports.immobility.summary.high")}
                </Typography>
                <Typography variant="h6" color="warning.main">
                  {summaryCounts.high}
                </Typography>
              </CardContent>
            </Card>
            <Card
              data-testid="immobility-medium-card"
              variant="outlined"
              sx={{ minWidth: 140 }}
            >
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {t("reports.immobility.summary.medium")}
                </Typography>
                <Typography variant="h6">{summaryCounts.medium}</Typography>
              </CardContent>
            </Card>
          </Stack>

          {/* Data Grid */}
          <Typography variant="subtitle1" fontWeight="bold">
            {t("reports.immobility.title")} ({report.totalStuckOrders})
          </Typography>
          {report.items.length > 0 ? (
            <DataGrid
              data-testid="immobility-grid"
              rows={gridRows}
              columns={columns}
              pageSizeOptions={[25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 100 } },
              }}
              autoHeight
              disableRowSelectionOnClick
              density="compact"
              getRowClassName={(params) => {
                const level = getSeverityLevel(params.row.hoursStuck)
                return `immobility-severity-${level}`
              }}
              sx={{
                "& .immobility-severity-critical": {
                  backgroundColor: getSeverityColor(13),
                },
                "& .immobility-severity-high": {
                  backgroundColor: getSeverityColor(9),
                },
                "& .immobility-severity-medium": {
                  backgroundColor: getSeverityColor(5),
                },
              }}
            />
          ) : (
            <Typography
              data-testid="immobility-empty"
              color="text.secondary"
            >
              {t("reports.immobility.empty")}
            </Typography>
          )}
        </>
      )}

      {!report && !isLoading && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 200,
            color: "text.secondary",
          }}
        >
          <Typography>{t("reports.immobility.selectDate")}</Typography>
        </Box>
      )}
    </Box>
  )
}
